import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useViewport } from '../../hooks/useViewport';
import { UserDashboardLayout } from '../../components/layout/UserDashboardLayout';
import { ResumeManagementCard } from '../../components/dashboard/ResumeManagementCard';
import { VideoManagementCard } from '../../components/dashboard/VideoManagementCard';
import { ApplicationProgressCard } from '../../components/dashboard/ApplicationProgressCard';
import { useUserResumes } from '../../hooks/unified/useResumeQueries';
import { useApplications } from '../../hooks/unified/useApplicationsQueries';
import { useConversations } from '../../hooks/unified/useMessagingQueries';

export function DashboardPage() {
  const { user, isAdmin } = useAuth();
  useViewport(); // Used for responsive behavior via CSS
  const { data: resumes, isLoading: resumesLoading } = useUserResumes();
  const { data: applications, isLoading: applicationsLoading } = useApplications();
  const { data: conversations } = useConversations();

  // Calculate stats from real data
  const totalResumes = resumes?.length || 0;
  const scannedResumes = resumes?.filter(r => r.status === 'scanned').length || 0;
  
  const totalApplications = applications?.length || 0;
  const activeApplications = applications?.filter(a => 
    !['rejected', 'withdrawn', 'hired'].includes(a.status)
  ).length || 0;
  const interviewApplications = applications?.filter(a => a.status === 'interview').length || 0;
  
  // Count total conversations (unread tracking would need ConversationWithMetadata)
  const totalConversations = conversations?.length || 0;

  // Get recent applications for activity feed
  const recentApplications = applications
    ?.filter(a => a.applied_at)
    .sort((a, b) => new Date(b.applied_at!).getTime() - new Date(a.applied_at!).getTime())
    .slice(0, 3) || [];

  const getStatusColor = (status: string): string => {
    const colors: Record<string, string> = {
      submitted: 'bg-blue-100 text-blue-700',
      reviewing: 'bg-yellow-100 text-yellow-700',
      interview: 'bg-purple-100 text-purple-700',
      offered: 'bg-green-100 text-green-700',
      accepted: 'bg-green-100 text-green-700',
      hired: 'bg-emerald-100 text-emerald-700',
      rejected: 'bg-red-100 text-red-700',
      withdrawn: 'bg-gray-100 text-gray-700',
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  return (
    <UserDashboardLayout>
      <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
        {/* Welcome Header - Mobile optimized */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <div className="flex items-center gap-2 sm:gap-3">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                Welcome back, {user?.name?.split(' ')[0] || 'there'}!
              </h1>
              {isAdmin && (
                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-primary-100 text-primary-700">
                  Admin
                </span>
              )}
            </div>
            <p className="text-sm sm:text-base text-gray-500 mt-1">Here's what's happening with your job search.</p>
          </div>
        </div>

        {/* Stats Grid - 2 columns on mobile, 4 on desktop */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {/* Resumes - Compact card styling for mobile */}
          <Link 
            to="/dashboard/resumes"
            className="group bg-white rounded-xl border border-gray-100 p-3 sm:p-5 hover:shadow-md hover:border-primary-200 transition-all min-h-[44px]"
          >
            <div className="flex items-center justify-between">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-primary-50 flex items-center justify-center group-hover:bg-primary-100 transition-colors">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <span className="text-xs text-gray-400 group-hover:text-primary-500">→</span>
            </div>
            <div className="mt-2 sm:mt-3">
              <p className="text-xl sm:text-2xl font-bold text-gray-900">{totalResumes}</p>
              <p className="text-xs sm:text-sm text-gray-500">Resumes</p>
              {scannedResumes > 0 && (
                <p className="text-xs text-primary-600 mt-0.5 sm:mt-1">{scannedResumes} AI scanned</p>
              )}
            </div>
          </Link>

          {/* Applications - Compact card styling for mobile */}
          <Link 
            to="/dashboard/applications"
            className="group bg-white rounded-xl border border-gray-100 p-3 sm:p-5 hover:shadow-md hover:border-success/30 transition-all min-h-[44px]"
          >
            <div className="flex items-center justify-between">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-success/10 flex items-center justify-center group-hover:bg-success/20 transition-colors">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <span className="text-xs text-gray-400 group-hover:text-success">→</span>
            </div>
            <div className="mt-2 sm:mt-3">
              <p className="text-xl sm:text-2xl font-bold text-gray-900">{totalApplications}</p>
              <p className="text-xs sm:text-sm text-gray-500">Applications</p>
              {activeApplications > 0 && (
                <p className="text-xs text-success mt-0.5 sm:mt-1">{activeApplications} active</p>
              )}
            </div>
          </Link>

          {/* Interviews - Compact card styling for mobile */}
          <div className="bg-white rounded-xl border border-gray-100 p-3 sm:p-5 min-h-[44px]">
            <div className="flex items-center justify-between">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-purple-50 flex items-center justify-center">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
            <div className="mt-2 sm:mt-3">
              <p className="text-xl sm:text-2xl font-bold text-gray-900">{interviewApplications}</p>
              <p className="text-xs sm:text-sm text-gray-500">Interviews</p>
              {interviewApplications > 0 && (
                <p className="text-xs text-purple-600 mt-0.5 sm:mt-1">Scheduled</p>
              )}
            </div>
          </div>

          {/* Messages - Compact card styling for mobile */}
          <Link 
            to="/dashboard/messages"
            className="group bg-white rounded-xl border border-gray-100 p-3 sm:p-5 hover:shadow-md hover:border-warning/30 transition-all relative min-h-[44px]"
          >
            <div className="flex items-center justify-between">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-warning/10 flex items-center justify-center group-hover:bg-warning/20 transition-colors">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-warning" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
              </div>
              <span className="text-xs text-gray-400 group-hover:text-warning">→</span>
            </div>
            <div className="mt-2 sm:mt-3">
              <p className="text-xl sm:text-2xl font-bold text-gray-900">{totalConversations}</p>
              <p className="text-xs sm:text-sm text-gray-500">Conversations</p>
            </div>
          </Link>
        </div>

        {/* Active Applications Progress */}
        <ApplicationProgressCard />

        {/* Two Column Layout - Single column on mobile */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          {/* Resume Management */}
          <div>
            {resumesLoading ? (
              <div className="bg-white rounded-xl border border-gray-100 p-4 sm:p-6">
                <div className="animate-pulse space-y-4">
                  <div className="h-5 bg-gray-200 rounded w-1/3"></div>
                  <div className="h-20 bg-gray-100 rounded"></div>
                </div>
              </div>
            ) : (
              <ResumeManagementCard />
            )}
          </div>

          {/* Recent Activity */}
          <div className="bg-white rounded-xl border border-gray-100 p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Recent Activity</h3>
            {applicationsLoading ? (
              <div className="animate-pulse space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-16 bg-gray-100 rounded-lg"></div>
                ))}
              </div>
            ) : recentApplications.length > 0 ? (
              <div className="space-y-3">
                {recentApplications.map(app => (
                  <Link
                    key={app.id}
                    to={`/dashboard/applications`}
                    className="flex items-center justify-between p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {app.job_title || 'Job Application'}
                      </p>
                      <p className="text-xs text-gray-500">{app.applied_at ? formatDate(app.applied_at) : ''}</p>
                    </div>
                    <span className={`ml-3 px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(app.status)}`}>
                      {app.status}
                    </span>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gray-100 flex items-center justify-center">
                  <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <p className="text-sm text-gray-500">No applications yet</p>
                <Link to="/jobs" className="text-sm text-primary-600 hover:text-primary-700 mt-1 inline-block">
                  Browse open positions →
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Video Management */}
        <VideoManagementCard />
      </div>
    </UserDashboardLayout>
  );
}
